import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/auth-provider";

type QueryState<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  refetch(): Promise<void>;
};

type QueryOptions = {
  enabled?: boolean;
};

function useQueryState<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
  enabled: boolean,
): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const requestSeq = useRef(0);

  const refetch = useCallback(async () => {
    const requestId = requestSeq.current + 1;
    requestSeq.current = requestId;
    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const nextData = await load();
      if (requestSeq.current === requestId) {
        setData(nextData);
      }
    } catch (caught) {
      if (requestSeq.current === requestId) {
        setError(caught instanceof Error ? caught.message : "request_failed");
      }
    } finally {
      if (requestSeq.current === requestId) {
        setLoading(false);
      }
    }
  }, [enabled, load]);

  useEffect(() => {
    void refetch();
  }, [refetch, ...deps]);

  return { data, error, loading, refetch };
}

export function useOptionalWorkhubQuery<T>(
  load: () => Promise<T>,
  deps: readonly unknown[] = [],
  options: QueryOptions = {},
): QueryState<T> {
  return useQueryState(load, deps, options.enabled ?? true);
}

export function useWorkhubQuery<T>(
  load: (token: string) => Promise<T>,
  deps: readonly unknown[] = [],
  options: QueryOptions = {},
): QueryState<T> {
  const auth = useAuth();
  const token = auth.status === "authenticated" ? auth.token : null;
  const loadWithToken = useCallback(() => {
    if (token === null) {
      throw new Error("Authenticated token is missing");
    }
    return load(token);
  }, [load, token]);
  const enabled = (options.enabled ?? true) && token !== null;

  return useQueryState(loadWithToken, deps, enabled);
}
